import { Component, Input, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Chart, BarController, BarElement, LinearScale, CategoryScale } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TDeviceUsageDTO } from '../../../models/TDeviceUsageDTO.model';

@Component({
  selector: 'app-consumption-view',
  imports: [CommonModule],
  templateUrl: './consumption-view.component.html',
  styleUrls: ['./consumption-view.component.scss']
})
export class ConsumptionViewComponent implements OnChanges, AfterViewInit, OnDestroy {

  @Input() data?: TDeviceUsageDTO;
  
  @Input() useMobileLayout: boolean = false;


  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef<HTMLCanvasElement>;

  public monthlyConsumption: number[] = [];
  public monthlyHistoryConsumption: number[] = [];
  public rangeMonts: string[] = [];

  private chart: Chart | undefined;
  private resizeTimeout: any;
  private resizeObserver: ResizeObserver | undefined;

  constructor(private hostRef: ElementRef) {}

  ngOnInit() {
    Chart.register(BarController, BarElement, CategoryScale, LinearScale, ChartDataLabels);
  }

  ngAfterViewInit(): void {
    // Инициализируем график после того, как view готов
    if (this.data) {
      setTimeout(() => this.updateChart(), 0);
    }

    // Отслеживаем изменение размера самого компонента
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        if (this.chart) {
          this.updateChart();
        }
      }, 250);
    });
    this.resizeObserver.observe(this.hostRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange && this.chartCanvas) {
      this.updateChart();
    }
  }

  /**
   * Вычисляет адаптивный размер шрифта на основе размера canvas
   */
  private getResponsiveFontSize(baseSize: number = 12): number {
    if (!this.chartCanvas?.nativeElement) {
      return baseSize;
    }

    const canvas = this.chartCanvas.nativeElement;
    const containerWidth = canvas.offsetWidth;
    const containerHeight = canvas.offsetHeight;

    // Используем минимальное измерение для расчета
    //const minDimension = Math.min(containerWidth, containerHeight);
    const minDimension = containerWidth;

    if (minDimension === 0) return baseSize; // диаграмма не видна

    // Масштабируем шрифт от базового размера
    // Для canvas шириной 600px будет использован базовый размер
    const scaleFactor = minDimension / 600;
    const fontSize = Math.max(4, Math.min(baseSize * scaleFactor, baseSize)); // Ограничиваем от 8 до baseSize

    return Math.round(fontSize);
  }

  /**
   * Вычисляет адаптивный размер шрифта для заголовков осей
   */
  private getAxisTitleFontSize(): number {
    return this.getResponsiveFontSize(16);
  }

  /**
   * Вычисляет адаптивный размер шрифта для тиков осей
   */
  private getAxisTicksFontSize(): number {
    return this.getResponsiveFontSize(12);
  }

  /**
   * Вычисляет адаптивный размер шрифта для подписей данных
   */
  private getDataLabelsFontSize(): number {
    return this.getResponsiveFontSize(11);
  }

  private updateChart(): void {
    if (!this.data || !this.chartCanvas) {
      return;
    }

    // Destroy existing chart if it exists
    if (this.chart) {
      this.chart.destroy();
    }

    // Сгруппировать расходы по месяцам

    const currentConsumption = this.data.listConsumptions.filter(a => !a.isHistory);
    this.monthlyConsumption = this.data.rangeMonts.map((month, index) => {
      return currentConsumption.reduce((total, dataset) => {
        return total + dataset.consumptions[index];
      }, 0); // Суммировать потребление всех устройств за каждый месяц
    });

    const historyConsumption = this.data.listConsumptions.filter(a => a.isHistory);
    this.monthlyHistoryConsumption = this.data.rangeMonts.map((month, index) => {
      return historyConsumption.reduce((total, dataset) => {
        return total + dataset.consumptions[index];
      }, 0); // Суммировать потребление всех устройств за каждый месяц
    });

    this.rangeMonts = this.data.rangeMonts;

    var datasets = [
      {
        label: 'Current Total Data Usage',
        data: this.monthlyConsumption, // Общий объем потребления в месяц
        backgroundColor: '#7ab956',    // Одинаковый цвет для планок
      }
    ];
    
    if (historyConsumption.length) {
      datasets.push(
        {
          label: 'History Total Data Usage',
          data: this.monthlyHistoryConsumption, // Общий объем потребления в месяц
          backgroundColor: '#808080', // Одинаковый цвет для планок
        }
      )
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      console.error('Cannot get canvas context');
      return;
    }

    // Вычисляем адаптивные размеры шрифтов
    const axisTitleFontSize = this.getAxisTitleFontSize();
    const axisTicksFontSize = this.getAxisTicksFontSize();
    const dataLabelsFontSize = this.getDataLabelsFontSize();

    const canvas = this.chartCanvas.nativeElement;
    const containerWidth = canvas.offsetWidth;
    const containerHeight = canvas.offsetHeight;

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.rangeMonts,
        datasets/*: [
          {
            label: 'Total Data Usage',
            data: this.monthlyConsumption,
            backgroundColor: backgroundColors,
            stack: 'Stack 0',
          },
        ]*/,
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 30,
            right: 20,
            bottom: 10,
            left: 10
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              display: true,
            },
            ticks: {
              callback: function (value) {
                return value.toLocaleString();
              },
              maxTicksLimit: 5,
              font: {
                size: axisTicksFontSize,
              }
            },
            title: {
              display: true,
              text: 'Total Usage in MB',
              color: '#000',
              font: {
                size: axisTitleFontSize,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: axisTicksFontSize,
              }
            },
            title: {
              display: true,
              text: 'Months',
              color: '#000',
              font: {
                size: axisTitleFontSize,
              },
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              font: {
                size: axisTicksFontSize,
              }
            }
          },
          tooltip: {
            enabled: true,
            bodyFont: {
              size: axisTicksFontSize,
            },
            titleFont: {
              size: axisTicksFontSize,
            },
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                label += `${context.parsed.y?.toLocaleString() ?? '0'} MB`;
                return label;
              },
            },
          },
          datalabels: {
            display: true,
            color: '#000',
            align: 'end',
            anchor: 'end',
            rotation: (containerWidth > 0 && containerWidth < 900) ? 310 : 0,
            font: {
              size: dataLabelsFontSize,
              weight: 'normal',
            },
            formatter: (value) => {
              return `${value.toLocaleString()} MB`;
            },
          },
        },
      },
      plugins: [ChartDataLabels],
    });
  }
}